import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import "./logs.css";

// ------------------------------------------------------------
// 1️⃣ STATUS HELPER (same logic as dashboard)
// ------------------------------------------------------------
const getStatusFromEvents = (events) => {
  const relevant = events.filter((e) =>
    ["ACKNOWLEDGED", "DISPUTED", "SILENT"].includes(e.event_type)
  );

  if (relevant.length === 0) return "Pending";

  relevant.sort((a, b) => {
    const aTime = a.created_at?.toDate?.() || 0;
    const bTime = b.created_at?.toDate?.() || 0;
    return bTime - aTime;
  });

  const latest = relevant[0];

  switch (latest.event_type) {
    case "ACKNOWLEDGED":
      return "Agreed";
    case "DISPUTED":
      return "Disputed";
    case "SILENT":
      return "Silent";
    default:
      return "Pending";
  }
};

// ------------------------------------------------------------
// 2️⃣ MAIN COMPONENT
// ------------------------------------------------------------
export default function DecisionHistory() {
  const [logs, setLogs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 🔥 Firebase listeners (same collections as dashboard)
  useEffect(() => {
    if (!auth.currentUser) return;

    const logsQuery = query(
      collection(db, "logs"),
      where("owner_id", "==", auth.currentUser.uid),
      orderBy("created_at", "desc")
    );

    const unsubLogs = onSnapshot(logsQuery, (snap) => {
      const list = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLogs(list);
    });

    const eventsQuery = query(
      collection(db, "log_events"),
      where("owner_id", "==", auth.currentUser.uid),
      orderBy("created_at", "desc")
    );

    const unsubEvents = onSnapshot(eventsQuery, (snap) => {
      const list = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEvents(list);
      setLoading(false);
    });

    return () => {
      unsubLogs();
      unsubEvents();
    };
  }, []);

  // ------------------------------------------------------------
  // 3️⃣ Build events by log
  // ------------------------------------------------------------
  const eventsByLog = {};
  events.forEach((e) => {
    if (!eventsByLog[e.log_id]) eventsByLog[e.log_id] = [];
    eventsByLog[e.log_id].push(e);
  });

  // ------------------------------------------------------------
  // 4️⃣ Build enriched logs with status + silent days
  // ------------------------------------------------------------
  const enrichedLogs = logs.map((log) => {
    const logEvents = eventsByLog[log.id] || [];
    const status = getStatusFromEvents(logEvents);

    let silentDays = 0;

    if (status === "Pending" || status === "Silent") {
      if (log.created_at) {
        const created = log.created_at.toDate();
        const today = new Date();
        const diff = Math.floor(
          (today - created) / (1000 * 60 * 60 * 24)
        );
        silentDays = diff;
      }
    }

    return {
      ...log,
      status,
      silentDays,
    };
  });

  // ------------------------------------------------------------
  // 5️⃣ Summary counts
  // ------------------------------------------------------------
  const total = enrichedLogs.length;
  const agreed = enrichedLogs.filter((l) => l.status === "Agreed").length;
  const disputed = enrichedLogs.filter((l) => l.status === "Disputed").length;
  const noResponse = enrichedLogs.filter(
    (l) => l.status === "Silent"
  ).length;
  const pending = enrichedLogs.filter(
    (l) => l.status === "Pending"
  ).length;

  const openLog = (id) => navigate(`/logs/${id}`);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading Decision History…</p>
      </div>
    );
  }

  return (
    <div className="content-area">
      <div className="content-card">

        {/* HEADER */}
        <div className="dashboard-header">
          <h1>Decision History</h1>
        </div>

        {/* SUMMARY CARDS */}
        <div className="decision-summary-grid">
          <SummaryCard title="Total Requests" value={total} color="#FFA500" />
          <SummaryCard title="Agreed" value={agreed} color="#4CAF50" />
          <SummaryCard title="Disputed" value={disputed} color="#F44336" />
          <SummaryCard title="Silent" value={noResponse} color="#9E9E9E" />
          <SummaryCard title="Pending" value={pending} color="#FFB300" />
        </div>

        {/* TABLE */}
        <div className="decision-table-wrapper">
          <table className="decision-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Requested On</th>
                <th>Status</th>
                <th>Silent For</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {enrichedLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.title || "Untitled"}</td>
                  <td>{log.log_type || "Manual"}</td>
                  <td>
                    {log.created_at
                      ? log.created_at.toDate().toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <StatusBadge status={log.status} />
                  </td>
                  <td>
                    {(log.status === "Pending" ||
                      log.status === "Silent") &&
                    log.silentDays > 0
                      ? `${log.silentDays} days`
                      : "-"}
                  </td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => openLog(log.id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {enrichedLogs.length === 0 && (
            <div className="empty-state">
              No decision history found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// 6️⃣ Small Components
// ------------------------------------------------------------
const SummaryCard = ({ title, value, color }) => (
  <div className="decision-card" style={{ borderTop: `4px solid ${color}` }}>
    <div className="decision-card-title">{title}</div>
    <div className="decision-card-value">{value}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    Agreed: "status-agreed",
    Disputed: "status-disputed",
    "silent": "status-Silent",
    Pending: "status-pending",
  };

  return <span className={`status-badge ${map[status]}`}>{status}</span>;
};
